import React, { KeyboardEvent, useState } from 'react'

import { TypedDocumentNode } from '@apollo/client'
import {
  BorderStyle,
  Box,
  Checkbox,
  CounterBadge,
  EnhancedDimension,
  Icon,
  SearchField,
} from '@factorialco/deprecated-design-system'
import { Exact, PaginatedQueryPageInfoFragment } from 'generated/resources/graphql'
import { xorBy } from 'lodash'

import LabeledInput from 'modules/core/components/LabeledInput'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCallback } from 'modules/core/hooks/useCallback'
import { UseQueryType } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { ClearValueButton } from '../Filter/base/ClearValue/ClearValueButton'
import { PaginatedQueryContainer } from './PaginatedQueryContainer'

interface EntitySelectorProps<
  TResult,
  Node extends { [key: string]: unknown },
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: Node }
> {
  document: TypedDocumentNode<TResult, Variables>
  variables: Omit<Exact<Variables>, 'search' | 'after'>
  extractConnection: (data: NonNullable<UseQueryType<TResult, Variables>['data']>) => {
    pageInfo: PaginatedQueryPageInfoFragment
    edges: Array<Edge>
  }
  renderEntity: (entity: Node) => JSX.Element
  value: Node[]
  onChange: (value: Node[]) => void
  onPopoverClose?: () => void
  renderValue: (value: Node[]) => JSX.Element
  disabled?: boolean
  error?: string
  mandatory?: boolean
  label?: string
  description?: string
  filterEntities?: (entities: Node[]) => Node[]
  alwaysOpen?: boolean
  width?: EnhancedDimension
  minWidth?: EnhancedDimension
  renderSkeleton?: () => JSX.Element
  hideBorder?: boolean
  hideSearchIcon?: boolean
  hideLabel?: boolean
}

export const MultipleEntitySelector = <
  TResult,
  Node extends { [key: string]: unknown },
  Variables extends { [key: string]: unknown },
  Edge extends { cursor: string; node: Node }
>({
  value,
  renderValue,
  onChange,
  document,
  renderEntity,
  variables,
  extractConnection,
  error,
  mandatory,
  label,
  description,
  filterEntities,
  width,
  minWidth,
  renderSkeleton,
  onPopoverClose,
  alwaysOpen = false,
  disabled = false,
  hideBorder = false,
  hideSearchIcon = false,
  hideLabel = false,
}: EntitySelectorProps<TResult, Node, Variables, Edge>) => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const { search, onSearch, debouncedSearch } = useDebouncedSearch()
  const [hoveringIndex, setHoveringIndex] = useState<string | null>(null)

  const unhover = useCallback(() => setHoveringIndex(null), [])
  const hover = useCallback((index: string) => setHoveringIndex(index), [])
  const borderStyle: BorderStyle | undefined = hideBorder
    ? undefined
    : { width: 's1', color: 'grey400', style: 'solid' }

  const handleToggle = () => {
    setPopoverOpen(!popoverOpen)
    if (popoverOpen) {
      onPopoverClose?.()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Escape') {
      setPopoverOpen(false)
      event.stopPropagation()
    }
  }

  const content = (
    <div aria-label="entity-selector-popover" onKeyDown={handleKeyDown}>
      <Box width={width} minWidth={minWidth}>
        <Box padding="s12">
          <SearchField variant="big" onSearch={onSearch} search={search} placeholder="" />
        </Box>
      </Box>
      <PaginatedQueryContainer<TResult, Node, Variables, Edge>
        document={document}
        height="s360"
        width={width}
        minWidth={minWidth}
        variables={{ ...variables, search: debouncedSearch }}
        extractConnection={extractConnection}
        filterEntities={filterEntities}
        loadingContent={
          renderSkeleton
            ? () => (
                <>
                  <BoxRow
                    id={'skeleton-1'}
                    hover={hover}
                    unhover={unhover}
                    hoveringIndex={hoveringIndex}
                  >
                    {renderSkeleton()}
                  </BoxRow>
                  <BoxRow
                    id={'skeleton-2'}
                    hover={hover}
                    unhover={unhover}
                    hoveringIndex={hoveringIndex}
                  >
                    {renderSkeleton()}
                  </BoxRow>
                  <BoxRow
                    id={'skeleton-3'}
                    hover={hover}
                    unhover={unhover}
                    hoveringIndex={hoveringIndex}
                  >
                    {renderSkeleton()}
                  </BoxRow>
                </>
              )
            : undefined
        }
        content={(node) => {
          const key = `${node.id}`
          const isSelected = !!value?.find((n) => `${n.id}` === key)
          const onNodeChange = () => onChange(xorBy(value || [], [node], 'id'))

          return (
            <BoxRow
              key={key}
              id={key}
              isSelected={isSelected}
              hover={hover}
              unhover={unhover}
              onChange={onNodeChange}
              hoveringIndex={hoveringIndex}
            >
              {renderEntity(node)}
            </BoxRow>
          )
        }}
      />

      {value.length > 0 && (
        <Box width="full" style={{ marginBottom: '-12px' }}>
          <ClearValueButton onClear={() => onChange([])}>
            {i18n.t('common.clear_selection')}
          </ClearValueButton>
        </Box>
      )}
    </div>
  )

  if (alwaysOpen) {
    return (
      <Box width={width} minWidth={minWidth}>
        <Box
          paddingY="s12"
          overflowY="hidden"
          overflowX="hidden"
          border={{ all: borderStyle }}
          borderRadius={{ all: 'abs012' }}
          background="white"
        >
          {content}
        </Box>
      </Box>
    )
  }

  return (
    <PopoverMenu type="dialog" onClick={handleToggle} open={popoverOpen}>
      <LabeledInput
        aria-label="entity-selector"
        readonly={disabled}
        error={error}
        mandatory={mandatory}
        label={label}
        hideLabel={hideLabel}
        description={description}
        value={value?.toString() ?? undefined}
      >
        <PopoverMenu.Trigger disabled={disabled}>
          <Box
            paddingX="s16"
            paddingY="s8"
            flexDirection="row"
            height="s40"
            alignItems="center"
            width="full"
            cursor="pointer"
          >
            <Box flexDirection="row" width="full" gap="s6">
              {(!hideSearchIcon || !!value?.length) && (
                <CounterBadge
                  disabled={disabled}
                  count={Number(value?.length)}
                  noCountIcon="search"
                />
              )}
              <Box overflowX="hidden" textAlign="left">
                {renderValue(value)}
              </Box>
            </Box>
            <Icon.Small icon="caret-down" />
          </Box>
        </PopoverMenu.Trigger>
      </LabeledInput>
      <PopoverMenu.Content width="full" side="bottom" align="center" offset={0}>
        {content}
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

const BoxRow = ({
  id,
  hover,
  unhover,
  hoveringIndex,
  isSelected,
  onChange,
  children,
}: React.PropsWithChildren<{
  id: string
  hover: (key: string) => void
  unhover: () => void
  hoveringIndex: string | null
  isSelected?: boolean
  onChange?: () => void
}>) => {
  return (
    <Box
      key={id}
      component="label"
      paddingX="s16"
      paddingY="s12"
      cursor="pointer"
      flexDirection="row"
      gap="s16"
      onMouseEnter={() => hover(id)}
      onMouseLeave={unhover}
      background={id == hoveringIndex ? 'grey200' : isSelected ? 'viridian100' : undefined}
    >
      <Checkbox
        size="large"
        checked={isSelected}
        disabled={onChange === undefined}
        onChange={onChange}
      />
      {children}
    </Box>
  )
}
